import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ResultRow } from './DetailedResultsTable'
import type { RunResult } from '../slices/runDetailsSlice'

interface CleanEvent {
  id: string
  type: string
  container_name: string
  timestamp: string
  data: string
}

interface AttemptEvents {
  containerName: string
  attemptNumber: number
  events: CleanEvent[]
}

interface FailureRunResult {
  task_id: string
  status: string
  llm_assessment: string
  dsq_claude_result: string
  dsq_claude: string
  dsq_codex_result: string
  dsq_codex: string
  grade_claude_result: string
  grade_claude: string
  grade_codex_result: string
  grade_codex: string
  root_cause_judge_result: string
  root_cause_judge: string
  cost: number
  time_seconds: number
  attempt: number
  assessed_at: string | null
  agent_name: string
  run_id: string
  run_name: string
}

interface FailureDetailSectionsProps {
  failure: {
    id: string
    short_id: string
    github_repository: string
    branch: string
    failing_changeset: string
    fixed_changeset: string
    test_file: string
    clean_branch: string
    clean_status: string
    clean_error: string
    refined_branch: string
    refined_status: string
    refined_error: string
    refined_recording_id: string
    refined_playwright_recording_id: string
    refined_error_message: string
    refined_backend_recordings: string
    refine_script_logs: string
    root_cause_status: string
    root_cause_error: string
    root_cause_locations: string
    root_cause_explanation: string
    root_cause_proof: string
    root_cause_judge_claude: string
    root_cause_judge_codex: string
    test_archive_url: string
    results_archive_url: string
    run_status: string
    run_error: string
    summarize_status: string
    summarize_error: string
    summarize_claude: string
    summarize_codex: string
    failure_run_results: FailureRunResult[]
  }
  cleanAttemptEvents: AttemptEvents[]
  cleanEventsLoading: boolean
  onToggleCleanEvents: (attemptIndex: number) => void
  expandedAttempts: Set<number>
  refineAttemptEvents: AttemptEvents[]
  refineEventsLoading: boolean
  onToggleRefineEvents: (attemptIndex: number) => void
  expandedRefineAttempts: Set<number>
  rootCauseAttemptEvents: AttemptEvents[]
  rootCauseEventsLoading: boolean
  onToggleRootCauseEvents: (attemptIndex: number) => void
  expandedRootCauseAttempts: Set<number>
  onRefreshStage?: (stage: string) => Promise<void>
  refreshingStage?: string | null
  onRefresh?: () => void
}

function formatTimestamp(ts: string): string {
  const date = new Date(ts)
  return date.toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

function parseData(data: string): Record<string, unknown> {
  try {
    return typeof data === 'string' ? JSON.parse(data) : data as Record<string, unknown>
  } catch {
    return {}
  }
}

function formatEventData(type: string, data: Record<string, unknown>): string {
  switch (type) {
    case 'container.started':
      return [
        data.revision && `rev: ${String(data.revision).slice(0, 8)}`,
      ].filter(Boolean).join(' · ') || 'Started'
    case 'container.stopped':
      return 'Stopped'
    case 'container.error':
      return data.error ? String(data.error).slice(0, 200) : 'Container creation failed'
    case 'message.done':
      return [
        data.cost_usd != null && `$${Number(data.cost_usd).toFixed(2)}`,
        data.duration_ms != null && `${(Number(data.duration_ms) / 1000).toFixed(1)}s`,
        data.num_turns != null && `${data.num_turns} turns`,
      ].filter(Boolean).join(' · ') || 'Done'
    case 'task.started':
      return [
        data.skill && String(data.skill).split('/').pop(),
        data.subtasks && `${(data.subtasks as string[]).length} subtasks`,
      ].filter(Boolean).join(' · ') || `attempt ${data.attempt ?? '?'}`
    case 'task.done': {
      const parts: string[] = []
      if (data.skill) parts.push(String(data.skill).split('/').pop()!)
      if (data.cost != null) parts.push(`$${Number(data.cost).toFixed(2)}`)
      if (data.failed) parts.push('FAILED')
      return parts.join(' · ') || 'Done'
    }
    case 'log':
      return data.line ? String(data.line).slice(0, 120) : ''
    default:
      return Object.keys(data).length > 0 ? JSON.stringify(data).slice(0, 120) : ''
  }
}

function getEventTypeClass(type: string): string {
  if (type === 'container.error') return 'event-type-error'
  if (type.startsWith('container.')) return 'event-type-container'
  if (type.startsWith('message.')) return 'event-type-message'
  if (type.startsWith('task.')) return 'event-type-task'
  if (type === 'log') return 'event-type-log'
  return ''
}

function getStatusClass(status: string): string {
  switch (status) {
    case 'running': return 'clean-status--running'
    case 'completed': return 'clean-status--completed'
    case 'failed': return 'clean-status--failed'
    default: return ''
  }
}

function CleanEventsTable({ events }: { events: CleanEvent[] }) {
  return (
    <table className="clean-events-table" aria-label="Container events">
      <thead>
        <tr>
          <th scope="col">Time</th>
          <th scope="col">Type</th>
          <th scope="col">Data</th>
        </tr>
      </thead>
      <tbody>
        {events.map((evt) => {
          const data = parseData(evt.data)
          return (
            <tr key={evt.id}>
              <td className="event-time">{formatTimestamp(evt.timestamp)}</td>
              <td>
                <span className={`event-type-badge ${getEventTypeClass(evt.type)}`}>
                  {evt.type}
                </span>
              </td>
              <td className="event-data">
                <span className={`event-data-text ${evt.type === 'log' ? 'event-data-log' : ''}`}>
                  {formatEventData(evt.type, data) || '-'}
                </span>
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

function AttemptsLog({
  attempts, expanded, loading, onToggle, testIdPrefix,
}: {
  attempts: AttemptEvents[]
  expanded: Set<number>
  loading: boolean
  onToggle: (i: number) => void
  testIdPrefix: string
}) {
  if (attempts.length === 0) return null
  return (
    <div className="clean-attempts-list">
      <dt className="meta-label">Attempts</dt>
      {attempts.map((attempt, index) => {
        const isExpanded = expanded.has(index)
        return (
          <div key={attempt.containerName} className="clean-attempt-item" data-testid={`${testIdPrefix}-attempt-${attempt.attemptNumber}`}>
            <button
              className="clean-attempt-toggle"
              onClick={() => onToggle(index)}
              aria-expanded={isExpanded}
              data-testid={`${testIdPrefix}-attempt-toggle-${attempt.attemptNumber}`}
            >
              <span className="clean-events-toggle-icon" aria-hidden="true">{isExpanded ? '\u25BC' : '\u25B6'}</span>
              <span>Attempt {attempt.attemptNumber}</span>
              <code className="clean-attempt-container">{attempt.containerName}</code>
            </button>
            {isExpanded && (
              <div className="clean-attempt-events">
                {loading ? (
                  <div className="clean-events-loading">Loading events...</div>
                ) : attempt.events.length === 0 ? (
                  <div className="clean-events-empty">No events received yet.</div>
                ) : (
                  <CleanEventsTable events={attempt.events} />
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function RunResultsExpanded({ results, runStatus, failureId, failureShortId, onRefresh }: { results: FailureRunResult[]; runStatus: string; failureId: string; failureShortId: string; onRefresh?: () => void }) {
  const mapped: (RunResult & { _run_id: string })[] = useMemo(() => results.map(r => ({
    task_id: r.task_id,
    failure_id: failureId,
    failure_title: null,
    failure_short_id: `${r.agent_name || failureShortId} #${r.attempt ?? 1}`,
    status: r.status as RunResult['status'],
    llm_assessment: r.llm_assessment || '',
    dsq_claude_result: r.dsq_claude_result || '',
    dsq_claude: r.dsq_claude || '',
    dsq_codex_result: r.dsq_codex_result || '',
    dsq_codex: r.dsq_codex || '',
    grade_claude_result: r.grade_claude_result || '',
    grade_claude: r.grade_claude || '',
    grade_codex_result: r.grade_codex_result || '',
    grade_codex: r.grade_codex || '',
    root_cause_judge_result: r.root_cause_judge_result || '',
    root_cause_judge: r.root_cause_judge || '',
    cost: r.cost ?? 0,
    time: r.time_seconds ?? 0,
    complexity: '',
    score: 0,
    logs: [],
    events: [],
    log_count: 1, // assume logs exist so expand button shows
    fix_started_at: null,
    verify_started_at: null,
    assessed_at: r.assessed_at || null,
    agent_id: null,
    agent_name: r.agent_name || null,
    attempt: r.attempt ?? 1,
    container_name: null,
    _run_id: r.run_id,
  })), [results])

  // First column always renders (chevron + failure_short_id which shows agent#attempt).
  // Don't show separate Agent/Attempt columns to avoid duplication.
  const colSpan = 4

  return (
    <div className="clean-branch-row clean-branch-row--stack">
      <dt className="meta-label">Run Results</dt>
      <dd>
        <div className="detailed-results-table-wrapper">
          <table className="detailed-results-table" aria-label="Failure run results" data-testid="run-results-table">
            <thead>
              <tr>
                <th>Agent</th>
                <th>Result</th>
                <th>Cost</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {mapped.map((r) => (
                <ResultRow
                  key={r.task_id}
                  result={r}
                  runId={r._run_id}
                  runStatus={runStatus || 'completed'}
                  showAgent={false}
                  showAttempt={false}
                  colSpan={colSpan}
                  onRefresh={onRefresh}
                />
              ))}
            </tbody>
          </table>
        </div>
      </dd>
    </div>
  )
}

function JudgeResults({ claude, codex }: { claude: string; codex: string }) {
  const claudeParsed = useMemo(() => {
    try { return JSON.parse(claude) as { accepted: boolean; reasoning: string } } catch { return null }
  }, [claude])
  const codexParsed = useMemo(() => {
    try { return JSON.parse(codex) as { accepted: boolean; reasoning: string } } catch { return null }
  }, [codex])
  const [expandedJudge, setExpandedJudge] = useState<string | null>(null)

  if (!claudeParsed && !codexParsed) return null

  return (
    <div className="rca-judges">
      {claudeParsed && (
        <div className="rca-judge-item">
          <button className="rca-judge-toggle" onClick={() => setExpandedJudge(expandedJudge === 'claude' ? null : 'claude')}>
            <span className="stage-section-toggle-icon" aria-hidden="true">{expandedJudge === 'claude' ? '\u25BC' : '\u25B6'}</span>
            <span className={`rca-judge-verdict ${claudeParsed.accepted ? 'rca-judge-accept' : 'rca-judge-reject'}`}>
              {claudeParsed.accepted ? 'ACCEPT' : 'REJECT'}
            </span>
            <span className="rca-judge-name">Claude</span>
          </button>
          {expandedJudge === 'claude' && (
            <pre className="rca-evidence-detail">{claudeParsed.reasoning}</pre>
          )}
        </div>
      )}
      {codexParsed && (
        <div className="rca-judge-item">
          <button className="rca-judge-toggle" onClick={() => setExpandedJudge(expandedJudge === 'codex' ? null : 'codex')}>
            <span className="stage-section-toggle-icon" aria-hidden="true">{expandedJudge === 'codex' ? '\u25BC' : '\u25B6'}</span>
            <span className={`rca-judge-verdict ${codexParsed.accepted ? 'rca-judge-accept' : 'rca-judge-reject'}`}>
              {codexParsed.accepted ? 'ACCEPT' : 'REJECT'}
            </span>
            <span className="rca-judge-name">Codex</span>
          </button>
          {expandedJudge === 'codex' && (
            <pre className="rca-evidence-detail">{codexParsed.reasoning}</pre>
          )}
        </div>
      )}
    </div>
  )
}

function EvidenceRow({ ev, index }: { ev: { type: string; tool?: string; params?: Record<string, unknown>; result?: string; file?: string; startLine?: number; endLine?: number; content?: string }; index: number }) {
  const [expanded, setExpanded] = useState(false)
  const label = ev.type === 'mcp'
    ? (() => { const p = JSON.stringify(ev.params || {}); return p.length > 80 ? `MCP: ${ev.tool}(${p.slice(0, 80)}…)` : `MCP: ${ev.tool}(${p})` })()
    : ev.type === 'source'
      ? `Source: ${ev.file} L${ev.startLine}-${ev.endLine}`
      : `Evidence ${index + 1}`
  const detail = ev.type === 'mcp' ? ev.result : ev.type === 'source' ? ev.content : null

  return (
    <div className="rca-evidence-row">
      <button
        className="rca-evidence-toggle"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span className="stage-section-toggle-icon" aria-hidden="true">{expanded ? '\u25BC' : '\u25B6'}</span>
        <span className="rca-evidence-label">{label}</span>
      </button>
      {expanded && detail && (
        <pre className="rca-evidence-detail">{String(detail)}</pre>
      )}
      {expanded && !detail && (
        <div className="rca-evidence-detail rca-evidence-empty">(no data)</div>
      )}
    </div>
  )
}

function RootCauseProof({ proof }: { proof: string }) {
  const parsed = useMemo(() => {
    try {
      return JSON.parse(proof) as { steps: Array<{ description: string; evidence: Array<{ type: string; tool?: string; params?: Record<string, unknown>; result?: string; file?: string; startLine?: number; endLine?: number; content?: string }> }> }
    } catch {
      return null
    }
  }, [proof])

  if (!parsed?.steps) {
    return <pre className="failure-detail-pre">{proof}</pre>
  }

  return (
    <ol className="refined-steps-list" data-testid="root-cause-proof-steps">
      {parsed.steps.map((step, i) => (
        <li key={i} className="refined-steps-item">
          <div className="rca-step-content">
            <span className="refined-steps-description">{step.description}</span>
            {step.evidence?.map((ev, j) => (
              <EvidenceRow key={j} ev={ev} index={j} />
            ))}
          </div>
        </li>
      ))}
    </ol>
  )
}

function FailureDetailSections({
  failure, cleanAttemptEvents, cleanEventsLoading,
  onToggleCleanEvents, expandedAttempts,
  refineAttemptEvents, refineEventsLoading,
  onToggleRefineEvents, expandedRefineAttempts,
  rootCauseAttemptEvents, rootCauseEventsLoading,
  onToggleRootCauseEvents, expandedRootCauseAttempts,
  onRefreshStage, refreshingStage, onRefresh,
}: FailureDetailSectionsProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev)
      if (next.has(section)) next.delete(section)
      else next.add(section)
      return next
    })
  }

  return (
    <div data-testid="failure-detail-sections">
      {/* ── Source Info ── */}
      <section className="failure-detail-section">
        <h2 className="failure-detail-section-title">Source</h2>
        <div className="failure-detail-repo">
          <a data-testid="failure-detail-repo-link" href={failure.github_repository} target="_blank" rel="noopener noreferrer">
            {failure.github_repository}
          </a>
        </div>
        <dl className="failure-detail-changesets">
          <div data-testid="changeset-branch" className="changeset-item">
            <dt className="changeset-label">Branch</dt>
            <dd><code className="changeset-value">{failure.branch}</code></dd>
          </div>
          <div data-testid="changeset-failing" className="changeset-item">
            <dt className="changeset-label">Failing</dt>
            <dd><code className="changeset-value">{failure.failing_changeset}</code></dd>
          </div>
          <div data-testid="changeset-fixed" className="changeset-item">
            <dt className="changeset-label">Fixed</dt>
            <dd><code className="changeset-value">{failure.fixed_changeset}</code></dd>
          </div>
        </dl>
        <div className="failure-detail-test-file-row">
          <span className="meta-label">Test File</span>
          <code data-testid="failure-detail-test-file" className="failure-detail-code">{failure.test_file}</code>
        </div>
        {(failure.test_archive_url || failure.results_archive_url) && (
          <div className="failure-detail-test-file-row" style={{ marginTop: '0.5rem', display: 'flex', gap: '1rem' }}>
            {failure.test_archive_url && (
              <a href={failure.test_archive_url} className="report-archive-link" download>Download Test Archive</a>
            )}
            {failure.results_archive_url && (
              <a href={failure.results_archive_url} className="report-archive-link" download>Download Results Archive</a>
            )}
          </div>
        )}
      </section>

      {/* ── Stage 1: Cleaning ── */}
      <section data-testid="clean-branch-section" className="failure-detail-section failure-detail-stage">
        <div className="stage-section-header">
          <button
            className="stage-section-toggle"
            onClick={() => toggleSection('cleaning')}
            aria-expanded={expandedSections.has('cleaning')}
            data-testid="clean-section-toggle"
          >
            <span className="stage-section-toggle-icon" aria-hidden="true">{expandedSections.has('cleaning') ? '\u25BC' : '\u25B6'}</span>
            <h2 className="failure-detail-section-title">Stage 1: Cleaning</h2>
            <span className={`clean-status-badge ${getStatusClass(failure.clean_status)}`} data-testid="clean-status">
              {failure.clean_status || 'not started'}
            </span>
          </button>
          {onRefreshStage && failure.clean_status && (
            <button
              className="stage-refresh-btn"
              onClick={(e) => { e.stopPropagation(); onRefreshStage('clean') }}
              disabled={refreshingStage === 'clean'}
              data-testid="clean-refresh-btn"
              title="Reset cleaning and later stages"
            >
              <span className={`stage-refresh-icon ${refreshingStage === 'clean' ? 'spinning' : ''}`}>&#x21bb;</span>
            </button>
          )}
        </div>
        {expandedSections.has('cleaning') && (
          <>
            <p className="failure-detail-hint">Produces a clean branch with a reproducible test failure.</p>
            <div className="clean-branch-content" aria-live="polite">
              {failure.clean_status === 'completed' && failure.clean_branch && (
                <div className="clean-branch-row">
                  <dt className="meta-label">Branch</dt>
                  <dd><code className="changeset-value">{failure.clean_branch}</code></dd>
                </div>
              )}

              {failure.clean_error && (
                <div className="clean-branch-row">
                  <dd><span className="clean-error-text" role="alert" data-testid="clean-error">{failure.clean_error}</span></dd>
                </div>
              )}

              <AttemptsLog attempts={cleanAttemptEvents} expanded={expandedAttempts} loading={cleanEventsLoading} onToggle={onToggleCleanEvents} testIdPrefix="clean" />
            </div>
          </>
        )}
      </section>

      {/* ── Stage 2: Refining ── */}
      <section data-testid="refined-branch-section" className="failure-detail-section failure-detail-stage">
        <div className="stage-section-header">
          <button
            className="stage-section-toggle"
            onClick={() => toggleSection('refining')}
            aria-expanded={expandedSections.has('refining')}
            data-testid="refined-section-toggle"
          >
            <span className="stage-section-toggle-icon" aria-hidden="true">{expandedSections.has('refining') ? '\u25BC' : '\u25B6'}</span>
            <h2 className="failure-detail-section-title">Stage 2: Refining</h2>
            <span className={`clean-status-badge ${getStatusClass(failure.refined_status)}`} data-testid="refined-status">
              {failure.refined_status || 'not started'}
            </span>
          </button>
          {onRefreshStage && failure.refined_status && (
            <button
              className="stage-refresh-btn"
              onClick={(e) => { e.stopPropagation(); onRefreshStage('refine') }}
              disabled={refreshingStage === 'refine'}
              data-testid="refine-refresh-btn"
              title="Reset refining and later stages"
            >
              <span className={`stage-refresh-icon ${refreshingStage === 'refine' ? 'spinning' : ''}`}>&#x21bb;</span>
            </button>
          )}
        </div>
        {expandedSections.has('refining') && (
          <>
            <p className="failure-detail-hint">Produces a refined branch with a Replay recording and error message for debugging.</p>
            <div className="clean-branch-content" aria-live="polite">
              {failure.refined_status === 'completed' && failure.refined_branch && (
                <div className="clean-branch-row">
                  <dt className="meta-label">Branch</dt>
                  <dd><code className="changeset-value">{failure.refined_branch}</code></dd>
                </div>
              )}

              {failure.refined_recording_id && (
                <div className="clean-branch-row">
                  <dt className="meta-label">Browser Recording</dt>
                  <dd><a href={`https://app.replay.io/recording/${failure.refined_recording_id}`} target="_blank" rel="noopener noreferrer" className="resource-link" data-testid="refined-recording-id">{failure.refined_recording_id}</a></dd>
                </div>
              )}

              {failure.refined_playwright_recording_id && (
                <div className="clean-branch-row">
                  <dt className="meta-label">Playwright Recording</dt>
                  <dd><a href={`https://app.replay.io/recording/${failure.refined_playwright_recording_id}`} target="_blank" rel="noopener noreferrer" className="resource-link" data-testid="refined-playwright-recording-id">{failure.refined_playwright_recording_id}</a></dd>
                </div>
              )}

              {failure.refined_backend_recordings && (() => {
                try {
                  const backends = JSON.parse(failure.refined_backend_recordings) as Array<{ handlerPath: string; recordingId?: string; requestId?: string; timestamp?: string; responseCode?: number; pending?: boolean; failed?: boolean; error?: string; reason?: string }>
                  if (backends.length === 0) return null
                  const successful = backends.filter(b => b.recordingId && !b.failed && !b.pending)
                  const pending = backends.filter(b => b.pending)
                  const failed = backends.filter(b => (b.failed || !b.recordingId) && !b.pending)
                  return (
                    <div className="clean-branch-row" data-testid="refined-backend-recordings">
                      <dt className="meta-label">Backend Recordings</dt>
                      <dd>
                        <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                          {successful.map((b, i) => (
                            <li key={`ok-${i}`} style={{ marginBottom: '0.3em' }}>
                              <code>{b.handlerPath}</code>
                              {b.responseCode != null && <span style={{ marginLeft: '0.5em', color: b.responseCode >= 400 ? 'var(--color-error, #ef4444)' : 'var(--color-success, #16a34a)' }}>{b.responseCode}</span>}
                              {b.timestamp && <span style={{ marginLeft: '0.5em', color: 'var(--color-text-secondary, #888)', fontSize: '0.9em' }}>{new Date(b.timestamp).toLocaleTimeString()}</span>}
                              {b.requestId && <span style={{ marginLeft: '0.5em' }}>{b.requestId}</span>}
                              {' '}
                              <a href={`https://app.replay.io/recording/${b.recordingId}`} target="_blank" rel="noopener noreferrer" className="resource-link">recording</a>
                            </li>
                          ))}
                          {pending.map((b, i) => (
                            <li key={`pending-${i}`} style={{ marginBottom: '0.3em', color: 'var(--color-text-secondary, #888)' }}>
                              <code>{b.handlerPath}</code>
                              {b.requestId && <span style={{ marginLeft: '0.5em' }}>{b.requestId}</span>}
                              <span style={{ marginLeft: '0.5em', fontStyle: 'italic' }}>— recording…</span>
                            </li>
                          ))}
                          {failed.map((b, i) => (
                            <li key={`fail-${i}`} style={{ marginBottom: '0.3em', color: 'var(--color-error, #ef4444)' }}>
                              <code>{b.handlerPath}</code>
                              {b.requestId && <span style={{ marginLeft: '0.5em' }}>{b.requestId}</span>}
                              <span style={{ marginLeft: '0.5em' }}>— not recorded{(b.error || b.reason) ? `: ${b.error || b.reason}` : ''}</span>
                            </li>
                          ))}
                        </ul>
                      </dd>
                    </div>
                  )
                } catch { return null }
              })()}

              {failure.refined_error_message && (
                <div className="clean-branch-row">
                  <dt className="meta-label">Error Message</dt>
                  <dd><pre className="failure-detail-pre refined-error-message" data-testid="refined-error-message">{failure.refined_error_message}</pre></dd>
                </div>
              )}

              {failure.refined_error && (
                <div className="clean-branch-row">
                  <dd><span className="clean-error-text" role="alert" data-testid="refined-error">{failure.refined_error}</span></dd>
                </div>
              )}

              {failure.refine_script_logs && (
                <div className="clean-branch-row">
                  <dt className="meta-label">Script Logs</dt>
                  <dd><pre className="failure-detail-pre refined-error-message" data-testid="refine-script-logs" style={{ maxHeight: '400px', overflow: 'auto' }}>{failure.refine_script_logs}</pre></dd>
                </div>
              )}

              <AttemptsLog attempts={refineAttemptEvents} expanded={expandedRefineAttempts} loading={refineEventsLoading} onToggle={onToggleRefineEvents} testIdPrefix="refine" />
            </div>
          </>
        )}
      </section>

      {/* ── Stage 3: Root Cause Analysis ── */}
      <section data-testid="root-cause-section" className="failure-detail-section failure-detail-stage">
        <div className="stage-section-header">
          <button
            className="stage-section-toggle"
            onClick={() => toggleSection('rootCause')}
            aria-expanded={expandedSections.has('rootCause')}
            data-testid="root-cause-section-toggle"
          >
            <span className="stage-section-toggle-icon" aria-hidden="true">{expandedSections.has('rootCause') ? '\u25BC' : '\u25B6'}</span>
            <h2 className="failure-detail-section-title">Stage 3: Root Cause Analysis</h2>
            <span className={`clean-status-badge ${getStatusClass(failure.root_cause_status)}`} data-testid="root-cause-status">
              {failure.root_cause_status || 'not started'}
            </span>
          </button>
          {onRefreshStage && (failure.root_cause_status || failure.refined_status === 'completed') && (
            <button
              className="stage-refresh-btn"
              onClick={(e) => { e.stopPropagation(); onRefreshStage('root_cause') }}
              disabled={refreshingStage === 'root_cause'}
              data-testid="root-cause-refresh-btn"
              title="Reset root cause analysis"
            >
              <span className={`stage-refresh-icon ${refreshingStage === 'root_cause' ? 'spinning' : ''}`}>&#x21bb;</span>
            </button>
          )}
        </div>
        {expandedSections.has('rootCause') && (
          <>
            <p className="failure-detail-hint">Identifies the root cause of the failure with structured proof verified by dual-model judging.</p>
            <div className="clean-branch-content" aria-live="polite">
              {failure.root_cause_locations && (() => {
                const locations: string[] = (() => { try { return JSON.parse(failure.root_cause_locations) } catch { return [] } })()
                return locations.length > 0 ? (
                  <div className="clean-branch-row">
                    <dt className="meta-label">Root Cause Locations</dt>
                    <dd><pre className="failure-detail-pre" data-testid="root-cause-text">{locations.join('\n')}</pre></dd>
                  </div>
                ) : null
              })()}

              {failure.root_cause_explanation && (
                <div className="clean-branch-row">
                  <dt className="meta-label">Explanation</dt>
                  <dd><pre className="failure-detail-pre">{failure.root_cause_explanation}</pre></dd>
                </div>
              )}

              {failure.root_cause_proof && (
                <div className="clean-branch-row">
                  <dt className="meta-label">Proof</dt>
                  <dd>
                    <RootCauseProof proof={failure.root_cause_proof} />
                  </dd>
                </div>
              )}

              {(failure.root_cause_judge_claude || failure.root_cause_judge_codex) && (
                <div className="clean-branch-row clean-branch-row--stack">
                  <dt className="meta-label">Judge Results</dt>
                  <dd>
                    <JudgeResults claude={failure.root_cause_judge_claude} codex={failure.root_cause_judge_codex} />
                  </dd>
                </div>
              )}

              {failure.root_cause_error && (
                <div className="clean-branch-row">
                  <dd><span className="clean-error-text" role="alert" data-testid="root-cause-error">{failure.root_cause_error}</span></dd>
                </div>
              )}

              <AttemptsLog attempts={rootCauseAttemptEvents} expanded={expandedRootCauseAttempts} loading={rootCauseEventsLoading} onToggle={onToggleRootCauseEvents} testIdPrefix="root-cause" />
            </div>
          </>
        )}
      </section>

      {/* ── Stage 4: Running Tests ── */}
      <section data-testid="run-section" className="failure-detail-section failure-detail-stage">
        <div className="stage-section-header">
          <button
            className="stage-section-toggle"
            onClick={() => toggleSection('run')}
            aria-expanded={expandedSections.has('run')}
            data-testid="run-section-toggle"
          >
            <span className="stage-section-toggle-icon" aria-hidden="true">{expandedSections.has('run') ? '\u25BC' : '\u25B6'}</span>
            <h2 className="failure-detail-section-title">Stage 4: Running Tests</h2>
            <span className={`clean-status-badge ${getStatusClass(failure.run_status)}`} data-testid="run-status">
              {failure.run_status || 'not started'}
            </span>
          </button>
          {onRefreshStage && failure.run_status && (
            <button
              className="stage-refresh-btn"
              onClick={(e) => { e.stopPropagation(); onRefreshStage('run') }}
              disabled={refreshingStage === 'run'}
              data-testid="run-refresh-btn"
              title="Reset run and later stages"
            >
              <span className={`stage-refresh-icon ${refreshingStage === 'run' ? 'spinning' : ''}`}>&#x21bb;</span>
            </button>
          )}
        </div>
        {expandedSections.has('run') && (
          <>
            <p className="failure-detail-hint">Runs agents against the failure to test whether they can produce a fix.</p>
            <div className="clean-branch-content" aria-live="polite">
              {failure.run_error && (
                <div className="clean-branch-row">
                  <dd><span className="clean-error-text" role="alert" data-testid="run-error">{failure.run_error}</span></dd>
                </div>
              )}

              {failure.failure_run_results && failure.failure_run_results.length > 0 && (
                <>
                  {failure.failure_run_results[0]?.run_id && (
                    <div className="clean-branch-row">
                      <dt className="meta-label">Run</dt>
                      <dd>
                        <Link to={`/runs/${failure.failure_run_results[0].run_id}`} className="meta-link">
                          {failure.failure_run_results[0].run_name || failure.failure_run_results[0].run_id}
                        </Link>
                      </dd>
                    </div>
                  )}
                  <RunResultsExpanded results={failure.failure_run_results} runStatus={failure.run_status} failureId={failure.id} failureShortId={failure.short_id} onRefresh={onRefresh} />
                </>
              )}
            </div>
          </>
        )}
      </section>

      {/* ── Stage 5: Summarize ── */}
      <section data-testid="summarize-section" className="failure-detail-section failure-detail-stage">
        <div className="stage-section-header">
          <button
            className="stage-section-toggle"
            onClick={() => toggleSection('summarize')}
            aria-expanded={expandedSections.has('summarize')}
            data-testid="summarize-section-toggle"
          >
            <span className="stage-section-toggle-icon" aria-hidden="true">{expandedSections.has('summarize') ? '\u25BC' : '\u25B6'}</span>
            <h2 className="failure-detail-section-title">Stage 5: Summarize</h2>
            <span className={`clean-status-badge ${getStatusClass(failure.summarize_status)}`} data-testid="summarize-status">
              {failure.summarize_status || 'not started'}
            </span>
          </button>
          {onRefreshStage && failure.summarize_status && (
            <button
              className="stage-refresh-btn"
              onClick={(e) => { e.stopPropagation(); onRefreshStage('summarize') }}
              disabled={refreshingStage === 'summarize'}
              data-testid="summarize-refresh-btn"
              title="Reset summarize stage"
            >
              <span className={`stage-refresh-icon ${refreshingStage === 'summarize' ? 'spinning' : ''}`}>&#x21bb;</span>
            </button>
          )}
        </div>
        {expandedSections.has('summarize') && (
          <>
            <p className="failure-detail-hint">Summarizes the run results and produces a final assessment of agent performance.</p>
            <div className="clean-branch-content" aria-live="polite">
              {failure.summarize_error && (
                <div className="clean-branch-row">
                  <dd><span className="clean-error-text" role="alert" data-testid="summarize-error">{failure.summarize_error}</span></dd>
                </div>
              )}
              {failure.summarize_claude && (
                <div className="clean-branch-row">
                  <dt className="meta-label">Claude</dt>
                  <dd><pre className="model-output-text" data-testid="summarize-claude">{failure.summarize_claude}</pre></dd>
                </div>
              )}
              {failure.summarize_codex && (
                <div className="clean-branch-row">
                  <dt className="meta-label">Codex</dt>
                  <dd><pre className="model-output-text" data-testid="summarize-codex">{failure.summarize_codex}</pre></dd>
                </div>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  )
}

export default FailureDetailSections
